// the newest OpenAI model is "gpt-4o" which was released May 13, 2024. do not change this unless explicitly requested by the user
const OPENAI_MODEL = "gpt-4o";

/**
 * This is a client-side utility for making requests to the OpenAI API via our backend
 * All OpenAI API calls should go through our server to maintain API key security
 */

import type { ChatMessage } from "@shared/types";
import { apiRequest } from "./fetchWithAuth";
import { getAiReasoningMode } from "./aiReasoningMode";

/**
 * Sends a message to the ChatGPT assistant
 */
export async function sendChatMessage(
  message: string, 
  context?: string, 
  additionalInfo?: Record<string, unknown>
): Promise<string> {
  try {
    const reasoningMode = getAiReasoningMode();
    const response = await apiRequest('/api/ai/chat', {
      method: 'POST',
      data: {
        message,
        context,
        reasoning_mode: reasoningMode,
        ...additionalInfo
      }
    });
    
    // apiRequest already returns parsed JSON, no need to call .json()
    return response.response || "No response from AI";
  } catch (error) {
    console.error('Error sending chat message:', error);
    return "Sorry, I couldn't process your request at this time.";
  }
}

/**
 * Generates a meeting summary from a transcript
 */
export async function generateMeetingSummary(
  transcript: string, 
  meetingType: string
): Promise<string> {
  return sendChatMessage(transcript, 'meeting', { meetingType });
}

/**
 * Generates coaching insights based on an employee's performance
 */
export async function generateCoachingInsights(
  notes: string, 
  employeeName: string
): Promise<string> {
  return sendChatMessage(notes, 'coaching', { employeeName });
}

/**
 * Generates a daily huddle recap with recommendations
 */
export async function generateDailyHuddleRecap(
  reportData: string, 
  location: string, 
  date: string
): Promise<string> {
  return sendChatMessage(reportData, 'huddle', { location, date });
}

/**
 * Placeholder function for direct OpenAI integration
 * This would be used if we were calling OpenAI directly from the client
 * but we're using our server as a proxy for security
 */
export async function directOpenAIRequest(messages: ChatMessage[]) {
  try {
    console.warn('Direct OpenAI requests from the client are not implemented. Use server-side endpoints instead.');
    return "Please use the server-side OpenAI endpoints.";
  } catch (error) {
    console.error('Error with direct OpenAI request:', error);
    return "Error: Direct OpenAI requests not supported.";
  }
}
